import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from "recharts";
import {
  Users, BookOpen, Video, MessageSquare, Heart,
  Clock, TrendingUp, Trophy, Activity, UserCheck, CalendarDays,
  Eye, Zap, Timer, ArrowUpDown, BarChart3,
} from "lucide-react";
import { LiveBroadcastIcon } from "@/components/live-broadcast-icon";
import { format } from "date-fns";

interface AnalyticsData {
  totals: {
    users: number;
    classes: number;
    sessions: number;
    recordings: number;
    attendance: number;
    messages: number;
    reactions: number;
    avgSessionDurationSec: number;
    totalRecordingDurationSec: number;
  };
  recent: {
    usersLast7d: number;
    usersLast30d: number;
    sessionsLast7d: number;
    sessionsLast30d: number;
    attendanceLast7d: number;
    attendanceLast30d: number;
  };
  growth: {
    users: { date: string; count: number }[];
    sessions: { date: string; count: number }[];
    recordings: { date: string; count: number }[];
    attendance: { date: string; count: number }[];
  };
  breakdowns: {
    sessionsByStatus: { status: string; count: number }[];
    classesByLanguage: { language: string; count: number }[];
    recordingsByStatus: { status: string; count: number }[];
    usersByRole: { role: string; count: number }[];
  };
  leaderboards: {
    topProviders: { providerId: string; providerName: string; sessionCount: number }[];
    topClasses: { classId: string; classTitle: string; providerName: string; sessionCount: number }[];
  };
  recentUsers: { id: string; name: string; email: string; role: string; createdAt: string }[];
  attendance: {
    avgPerSession: number;
    perSession: {
      sessionId: string;
      classTitle: string;
      providerName: string;
      sessionDate: string;
      sessionStatus: string;
      attendeeCount: number;
      uniqueViewers: number;
      peakConcurrent: number;
      avgWatchDurationSec: number;
    }[];
  };
  providerAnalytics: {
    providerId: string;
    providerName: string;
    totalSessions: number;
    totalUniqueAttendees: number;
    avgAttendeesPerSession: number;
    totalMessages: number;
    totalReactions: number;
    avgSessionDurationSec: number;
  }[];
}

const PIE_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2, 217 91% 60%))",
  "hsl(var(--chart-3, 142 76% 36%))",
  "hsl(var(--chart-4, 43 96% 56%))",
  "hsl(var(--chart-5, 346 77% 49%))",
  "hsl(280 65% 60%)",
  "hsl(190 80% 45%)",
  "hsl(30 90% 55%)",
  "hsl(160 60% 45%)",
  "hsl(240 50% 55%)",
  "hsl(320 70% 50%)",
  "hsl(100 60% 40%)",
  "hsl(200 75% 50%)",
];

function formatDuration(seconds: number) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function cumulativeGrowth(data: { date: string; count: number }[]) {
  let total = 0;
  return data.map(d => {
    total += d.count;
    return { date: d.date, count: d.count, cumulative: total };
  });
}

export default function AnalyticsTab() {
  const { t } = useTranslation();

  const { data: analytics, isLoading } = useQuery<AnalyticsData>({
    queryKey: ["/api/admin/analytics"],
  });

  const [providerSortBy, setProviderSortBy] = useState<"totalUniqueAttendees" | "totalSessions">("totalUniqueAttendees");

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="status-analytics-loading">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="glass-panel p-8 text-center text-muted-foreground">
        {t("admin.noAnalytics", { defaultValue: "Failed to load analytics data" })}
      </div>
    );
  }

  const { totals, recent, growth, breakdowns, leaderboards, recentUsers } = analytics;
  const attendance = analytics.attendance ?? { avgPerSession: 0, perSession: [] };
  const providerAnalytics = analytics.providerAnalytics ?? [];
  const sortedProviders = [...providerAnalytics].sort((a, b) => b[providerSortBy] - a[providerSortBy]);

  const kpiCards = [
    { label: t("admin.totalUsers", { defaultValue: "Total Users" }), value: totals.users, icon: Users, sub: `+${recent.usersLast7d} this week`, color: "text-blue-400" },
    { label: t("admin.totalClasses", { defaultValue: "Total Classes" }), value: totals.classes, icon: BookOpen, sub: null, color: "text-emerald-400" },
    { label: t("admin.totalSessions", { defaultValue: "Total Sessions" }), value: totals.sessions, icon: CalendarDays, sub: `+${recent.sessionsLast7d} this week`, color: "text-purple-400" },
    { label: t("admin.totalRecordings", { defaultValue: "Recordings" }), value: totals.recordings, icon: Video, sub: formatDuration(totals.totalRecordingDurationSec) + " total", color: "text-orange-400" },
    { label: t("admin.totalAttendance", { defaultValue: "Attendance" }), value: totals.attendance, icon: Activity, sub: `+${recent.attendanceLast7d ?? 0} this week · avg ${attendance.avgPerSession ?? 0}/session`, color: "text-pink-400" },
    { label: t("admin.totalMessages", { defaultValue: "Chat Messages" }), value: totals.messages, icon: MessageSquare, sub: null, color: "text-cyan-400" },
    { label: t("admin.totalReactions", { defaultValue: "Reactions" }), value: totals.reactions, icon: Heart, sub: null, color: "text-red-400" },
    { label: t("admin.avgDuration", { defaultValue: "Avg Duration" }), value: formatDuration(totals.avgSessionDurationSec), icon: Clock, sub: "per session", color: "text-amber-400" },
  ];

  const userGrowthConfig: ChartConfig = {
    cumulative: { label: t("admin.totalUsers", { defaultValue: "Total Users" }), color: "hsl(var(--primary))" },
    count: { label: t("admin.newUsers", { defaultValue: "New Users" }), color: "hsl(var(--chart-2, 217 91% 60%))" },
  };

  const sessionGrowthConfig: ChartConfig = {
    cumulative: { label: t("admin.totalSessions", { defaultValue: "Total Sessions" }), color: "hsl(280 65% 60%)" },
    count: { label: t("admin.newSessions", { defaultValue: "New Sessions" }), color: "hsl(var(--chart-3, 142 76% 36%))" },
  };

  const attendanceGrowthConfig: ChartConfig = {
    count: { label: t("admin.dailyAttendees", { defaultValue: "Daily Attendees" }), color: "hsl(330 70% 55%)" },
  };

  const userGrowthData = cumulativeGrowth(growth.users).slice(-30);
  const sessionGrowthData = cumulativeGrowth(growth.sessions).slice(-30);
  const attendanceGrowthData = cumulativeGrowth(growth.attendance).slice(-30);

  const perSessionBarConfig: ChartConfig = {
    attendeeCount: { label: t("admin.attendees", { defaultValue: "Attendees" }), color: "hsl(var(--primary))" },
  };

  return (
    <div className="space-y-6" data-testid="analytics-container">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {kpiCards.map((kpi, i) => (
          <div
            key={i}
            className="glass-panel-sm p-4 flex flex-col gap-1"
            data-testid={`kpi-card-${i}`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">
                {kpi.label}
              </span>
              <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
            </div>
            <div className="text-2xl font-bold tracking-tight" data-testid={`kpi-value-${i}`}>
              {typeof kpi.value === "number" ? kpi.value.toLocaleString() : kpi.value}
            </div>
            {kpi.sub && (
              <span className="text-[11px] text-muted-foreground">{kpi.sub}</span>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-panel-sm p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" data-testid="text-user-growth-title">
            <TrendingUp className="h-4 w-4 text-blue-400" />
            {t("admin.userGrowth", { defaultValue: "User Growth" })}
          </h3>
          {userGrowthData.length > 0 ? (
            <ChartContainer config={userGrowthConfig} className="h-[220px] w-full">
              <AreaChart data={userGrowthData}>
                <defs>
                  <linearGradient id="userGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.3} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) => {
                    try { return format(new Date(v), "MMM d"); } catch { return v; }
                  }}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area
                  type="monotone"
                  dataKey="cumulative"
                  stroke="hsl(var(--primary))"
                  fill="url(#userGrad)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ChartContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
              {t("admin.noData", { defaultValue: "No data yet" })}
            </div>
          )}
        </div>

        <div className="glass-panel-sm p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" data-testid="text-session-growth-title">
            <TrendingUp className="h-4 w-4 text-purple-400" />
            {t("admin.sessionGrowth", { defaultValue: "Session Growth" })}
          </h3>
          {sessionGrowthData.length > 0 ? (
            <ChartContainer config={sessionGrowthConfig} className="h-[220px] w-full">
              <AreaChart data={sessionGrowthData}>
                <defs>
                  <linearGradient id="sessionGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(280 65% 60%)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="hsl(280 65% 60%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.3} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(v: string) => {
                    try { return format(new Date(v), "MMM d"); } catch { return v; }
                  }}
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Area
                  type="monotone"
                  dataKey="cumulative"
                  stroke="hsl(280 65% 60%)"
                  fill="url(#sessionGrad)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ChartContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-muted-foreground text-sm">
              {t("admin.noData", { defaultValue: "No data yet" })}
            </div>
          )}
        </div>
      </div>

      <div className="glass-panel-sm p-4">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" data-testid="text-attendance-growth-title">
          <UserCheck className="h-4 w-4 text-pink-400" />
          {t("admin.attendanceGrowth", { defaultValue: "Attendees Growth (Daily)" })}
        </h3>
        {attendanceGrowthData.length > 0 ? (
          <ChartContainer config={attendanceGrowthConfig} className="h-[250px] w-full">
            <BarChart data={attendanceGrowthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.3} />
              <XAxis
                dataKey="date"
                tickFormatter={(v: string) => {
                  try { return format(new Date(v), "MMM d"); } catch { return v; }
                }}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar
                dataKey="count"
                fill="hsl(330 70% 55%)"
                radius={[4, 4, 0, 0]}
                name="count"
              />
            </BarChart>
          </ChartContainer>
        ) : (
          <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">
            {t("admin.noData", { defaultValue: "No data yet" })}
          </div>
        )}
      </div>

      <div className="glass-panel-sm p-4">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" data-testid="text-per-session-attendance-title">
          <CalendarDays className="h-4 w-4 text-indigo-400" />
          {t("admin.perSessionAttendance", { defaultValue: "Attendees Per Session" })}
          {attendance.perSession.length > 0 && (
            <Badge variant="secondary" className="text-[10px] ml-auto font-mono">
              avg {attendance.avgPerSession}
            </Badge>
          )}
        </h3>
        {attendance.perSession.length > 0 ? (
          <>
            <ChartContainer config={perSessionBarConfig} className="h-[220px] w-full mb-4">
              <BarChart data={attendance.perSession.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.3} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <YAxis
                  dataKey="classTitle"
                  type="category"
                  tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                  axisLine={false}
                  tickLine={false}
                  width={120}
                  tickFormatter={(v: string) => v.length > 18 ? v.slice(0, 16) + "…" : v}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar
                  dataKey="attendeeCount"
                  fill="hsl(var(--primary))"
                  radius={[0, 4, 4, 0]}
                  name="attendeeCount"
                />
              </BarChart>
            </ChartContainer>

            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {attendance.perSession.map((s, i) => (
                <div
                  key={s.sessionId}
                  className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/10 border border-white/5"
                  data-testid={`attendance-session-${i}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium truncate">{s.classTitle}</span>
                      <Badge
                        variant={s.sessionStatus === "LIVE" ? "default" : s.sessionStatus === "ENDED" ? "secondary" : "outline"}
                        className="text-[10px] shrink-0"
                      >
                        {s.sessionStatus === "LIVE" && <LiveBroadcastIcon size={11} color="white" className="mr-1" />}
                        {s.sessionStatus}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {s.providerName} &middot; {s.sessionDate ? format(new Date(s.sessionDate), "MMM d, h:mm a") : "—"}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 shrink-0 ml-3">
                    <div className="text-center">
                      <div className="flex items-center gap-1">
                        <UserCheck className="h-3 w-3 text-pink-400" />
                        <span className="text-lg font-bold tabular-nums" data-testid={`attendance-count-${i}`}>
                          {s.attendeeCount}
                        </span>
                      </div>
                      <span className="text-[9px] text-muted-foreground">attendees</span>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3 text-blue-400" />
                        <span className="text-sm font-bold tabular-nums" data-testid={`unique-viewers-${i}`}>
                          {s.uniqueViewers}
                        </span>
                      </div>
                      <span className="text-[9px] text-muted-foreground">viewers</span>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1">
                        <Zap className="h-3 w-3 text-amber-400" />
                        <span className="text-sm font-bold tabular-nums" data-testid={`peak-concurrent-${i}`}>
                          {s.peakConcurrent}
                        </span>
                      </div>
                      <span className="text-[9px] text-muted-foreground">peak</span>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1">
                        <Timer className="h-3 w-3 text-emerald-400" />
                        <span className="text-sm font-bold tabular-nums" data-testid={`avg-watch-duration-${i}`}>
                          {s.avgWatchDurationSec > 0 ? formatDuration(s.avgWatchDurationSec) : "—"}
                        </span>
                      </div>
                      <span className="text-[9px] text-muted-foreground">avg watch</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="h-[120px] flex items-center justify-center text-muted-foreground text-sm">
            {t("admin.noAttendanceData", { defaultValue: "No attendance data yet" })}
          </div>
        )}
      </div>

      <div className="glass-panel-sm p-4" data-testid="provider-analytics-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-2" data-testid="text-provider-analytics-title">
            <BarChart3 className="h-4 w-4 text-violet-400" />
            {t("admin.providerAnalytics", { defaultValue: "Provider Analytics" })}
            {sortedProviders.length > 0 && (
              <Badge variant="secondary" className="text-[10px] font-mono">
                {sortedProviders.length} providers
              </Badge>
            )}
          </h3>
          {sortedProviders.length > 1 && (
            <button
              onClick={() => setProviderSortBy(prev => prev === "totalUniqueAttendees" ? "totalSessions" : "totalUniqueAttendees")}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              data-testid="button-sort-providers"
            >
              <ArrowUpDown className="h-3 w-3" />
              {providerSortBy === "totalUniqueAttendees" ? "by attendees" : "by sessions"}
            </button>
          )}
        </div>
        {sortedProviders.length > 0 ? (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {sortedProviders.map((p, i) => (
              <div
                key={p.providerId}
                className="p-3 rounded-lg bg-muted/10 border border-white/5"
                data-testid={`provider-analytics-card-${i}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold w-5 text-center ${
                      i === 0 ? "text-amber-400" : i === 1 ? "text-gray-400" : i === 2 ? "text-orange-400" : "text-muted-foreground"
                    }`}>
                      #{i + 1}
                    </span>
                    <span className="text-sm font-semibold" data-testid={`provider-name-${i}`}>{p.providerName}</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  <div className="text-center p-1.5 rounded bg-muted/20">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <CalendarDays className="h-3 w-3 text-purple-400" />
                      <span className="text-sm font-bold tabular-nums" data-testid={`provider-sessions-${i}`}>{p.totalSessions}</span>
                    </div>
                    <span className="text-[9px] text-muted-foreground">sessions</span>
                  </div>
                  <div className="text-center p-1.5 rounded bg-muted/20">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <Users className="h-3 w-3 text-blue-400" />
                      <span className="text-sm font-bold tabular-nums" data-testid={`provider-attendees-${i}`}>{p.totalUniqueAttendees}</span>
                    </div>
                    <span className="text-[9px] text-muted-foreground">attendees</span>
                  </div>
                  <div className="text-center p-1.5 rounded bg-muted/20">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <Activity className="h-3 w-3 text-emerald-400" />
                      <span className="text-sm font-bold tabular-nums" data-testid={`provider-avg-attendees-${i}`}>{p.avgAttendeesPerSession}</span>
                    </div>
                    <span className="text-[9px] text-muted-foreground">avg/session</span>
                  </div>
                  <div className="text-center p-1.5 rounded bg-muted/20">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <MessageSquare className="h-3 w-3 text-cyan-400" />
                      <span className="text-sm font-bold tabular-nums" data-testid={`provider-messages-${i}`}>{p.totalMessages}</span>
                    </div>
                    <span className="text-[9px] text-muted-foreground">messages</span>
                  </div>
                  <div className="text-center p-1.5 rounded bg-muted/20">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <Heart className="h-3 w-3 text-red-400" />
                      <span className="text-sm font-bold tabular-nums" data-testid={`provider-reactions-${i}`}>{p.totalReactions}</span>
                    </div>
                    <span className="text-[9px] text-muted-foreground">reactions</span>
                  </div>
                  <div className="text-center p-1.5 rounded bg-muted/20">
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <Clock className="h-3 w-3 text-amber-400" />
                      <span className="text-sm font-bold tabular-nums" data-testid={`provider-avg-duration-${i}`}>{formatDuration(p.avgSessionDurationSec)}</span>
                    </div>
                    <span className="text-[9px] text-muted-foreground">avg duration</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-[120px] flex items-center justify-center text-muted-foreground text-sm">
            {t("admin.noData", { defaultValue: "No data yet" })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="glass-panel-sm p-4">
          <h3 className="text-sm font-semibold mb-3">
            {t("admin.usersByRole", { defaultValue: "Users by Role" })}
          </h3>
          {breakdowns.usersByRole.length > 0 ? (
            <div className="space-y-2">
              {breakdowns.usersByRole.map((item, i) => (
                <div key={item.role} className="flex items-center justify-between" data-testid={`breakdown-role-${item.role}`}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                    />
                    <span className="text-sm">{item.role}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs font-mono">
                    {item.count}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-4">
              {t("admin.noData", { defaultValue: "No data yet" })}
            </div>
          )}
        </div>

        <div className="glass-panel-sm p-4">
          <h3 className="text-sm font-semibold mb-3">
            {t("admin.sessionsByStatus", { defaultValue: "Sessions by Status" })}
          </h3>
          {breakdowns.sessionsByStatus.length > 0 ? (
            <div className="space-y-2">
              {breakdowns.sessionsByStatus.map((item, i) => {
                const total = breakdowns.sessionsByStatus.reduce((s, x) => s + x.count, 0);
                const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                return (
                  <div key={item.status} data-testid={`breakdown-status-${item.status}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm">{item.status}</span>
                      <span className="text-xs text-muted-foreground font-mono">{item.count} ({pct}%)</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted/30 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-4">
              {t("admin.noData", { defaultValue: "No data yet" })}
            </div>
          )}
        </div>

        <div className="glass-panel-sm p-4">
          <h3 className="text-sm font-semibold mb-3">
            {t("admin.classesByLanguage", { defaultValue: "Classes by Language" })}
          </h3>
          {breakdowns.classesByLanguage.length > 0 ? (
            <div className="space-y-2">
              {breakdowns.classesByLanguage
                .sort((a, b) => b.count - a.count)
                .slice(0, 8)
                .map((item, i) => (
                  <div key={item.language} className="flex items-center justify-between" data-testid={`breakdown-lang-${item.language}`}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full shrink-0"
                        style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
                      />
                      <span className="text-sm capitalize">{item.language}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs font-mono">
                      {item.count}
                    </Badge>
                  </div>
                ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-4">
              {t("admin.noData", { defaultValue: "No data yet" })}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-panel-sm p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" data-testid="text-top-providers-title">
            <Trophy className="h-4 w-4 text-amber-400" />
            {t("admin.topProviders", { defaultValue: "Top Providers" })}
          </h3>
          {leaderboards.topProviders.length > 0 ? (
            <div className="space-y-2">
              {leaderboards.topProviders.map((p, i) => (
                <div
                  key={p.providerId}
                  className="flex items-center justify-between py-1.5"
                  data-testid={`leaderboard-provider-${i}`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className={`text-xs font-bold w-5 text-center ${
                      i === 0 ? "text-amber-400" : i === 1 ? "text-gray-400" : i === 2 ? "text-orange-400" : "text-muted-foreground"
                    }`}>
                      #{i + 1}
                    </span>
                    <span className="text-sm font-medium">{p.providerName}</span>
                  </div>
                  <Badge variant="outline" className="text-xs font-mono">
                    {p.sessionCount} {t("admin.sessionCount", { defaultValue: "sessions" })}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-4">
              {t("admin.noData", { defaultValue: "No data yet" })}
            </div>
          )}
        </div>

        <div className="glass-panel-sm p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" data-testid="text-top-classes-title">
            <Trophy className="h-4 w-4 text-emerald-400" />
            {t("admin.topClasses", { defaultValue: "Top Classes" })}
          </h3>
          {leaderboards.topClasses.length > 0 ? (
            <div className="space-y-2">
              {leaderboards.topClasses.map((c, i) => (
                <div
                  key={c.classId}
                  className="flex items-center justify-between py-1.5"
                  data-testid={`leaderboard-class-${i}`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={`text-xs font-bold w-5 text-center shrink-0 ${
                      i === 0 ? "text-amber-400" : i === 1 ? "text-gray-400" : i === 2 ? "text-orange-400" : "text-muted-foreground"
                    }`}>
                      #{i + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{c.classTitle}</p>
                      <p className="text-[11px] text-muted-foreground">{c.providerName}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs font-mono shrink-0 ml-2">
                    {c.sessionCount}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground text-center py-4">
              {t("admin.noData", { defaultValue: "No data yet" })}
            </div>
          )}
        </div>
      </div>

      <div className="glass-panel-sm p-4">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" data-testid="text-recent-users-title">
          <Users className="h-4 w-4 text-blue-400" />
          {t("admin.recentSignups", { defaultValue: "Recent Signups" })}
        </h3>
        {recentUsers.length > 0 ? (
          <div className="space-y-2">
            {recentUsers.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-1.5" data-testid={`recent-user-${u.id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold shrink-0">
                    {u.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{u.email}</p>
                  </div>
                </div>
                <div className="text-right shrink-0 ml-2">
                  <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {u.createdAt ? format(new Date(u.createdAt), "MMM d") : "—"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground text-center py-4">
            {t("admin.noData", { defaultValue: "No data yet" })}
          </div>
        )}
      </div>
    </div>
  );
}
